import { useEffect, useMemo, useRef } from "react";
import {
  MapContainer,
  Marker,
  Pane,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import iconoMapa from "../assets/images/ui/icono-mapa.png";
import { getImagenPreviewUrl, hasImagen } from "../utils/api";
import { useMapSettings } from "../context/MapSettingsContext";
import "./VolcanMapa.css";

const LEAFLET_ATTRIBUTION =
  '<a href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a>';

const COLOMBIA_VOLCANES_CENTER = [3.0, -74.6];
const COLOMBIA_VOLCANES_ZOOM = 6.5;
const MAP_EAST_SHIFT_LNG = 1.3;

function parseCoords(localizacion) {
  if (!localizacion) return null;
  const s = String(localizacion)
    .replaceAll("°", "")
    .replaceAll(",", " ")
    .trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function formatCoords(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return "";
  const lat = coords[0];
  const lng = coords[1];
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `${lat.toFixed(2)}, ${lng.toFixed(2)}`;
}

function idsMatch(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

const volcanIcon = L.icon({
  iconUrl: iconoMapa,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
  popupAnchor: [0, -26],
});

function MapClearOnBackgroundClick({ onClear }) {
  useMapEvents({
    click: () => onClear?.(),
  });
  return null;
}

function MapPopupCloseSync({ selectedVolcanId }) {
  const map = useMap();
  useEffect(() => {
    if (selectedVolcanId === undefined) return;
    if (selectedVolcanId === null) {
      map.closePopup();
    }
  }, [selectedVolcanId, map]);
  return null;
}

function MapInitialView({ points }) {
  const map = useMap();
  const hasFittedRef = useRef(false);

  useEffect(() => {
    if (hasFittedRef.current) return;

    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map((v) => v.__coords));
      map.fitBounds(bounds, {
        paddingTopLeft: [160, 48],
        paddingBottomRight: [24, 48],
        maxZoom: 6.8,
        animate: false,
      });

      const center = map.getCenter();
      map.setView(
        [center.lat, center.lng + MAP_EAST_SHIFT_LNG],
        map.getZoom(),
        { animate: false },
      );
    } else {
      map.setView(COLOMBIA_VOLCANES_CENTER, COLOMBIA_VOLCANES_ZOOM, {
        animate: false,
      });
    }

    hasFittedRef.current = true;
  }, [map, points]);

  return null;
}

const POPUP_BLUR_DELAY_MS = 400;

function VolcanMarker({
  volcan,
  coordsText,
  selectedVolcanId,
  onMoreInfo,
  onVolcanFocus,
  onVolcanBlur,
}) {
  const markerRef = useRef(null);
  const blurTimerRef = useRef(null);
  const map = useMap();

  const lat = volcan.__coords[0];
  const lng = volcan.__coords[1];

  const cancelBlur = () => {
    if (blurTimerRef.current != null) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  };

  const scheduleBlur = () => {
    cancelBlur();
    blurTimerRef.current = window.setTimeout(() => {
      blurTimerRef.current = null;
      onVolcanBlur?.(volcan.id);
    }, POPUP_BLUR_DELAY_MS);
  };

  const handleMarkerFocus = () => {
    cancelBlur();
    onVolcanFocus?.(volcan);
    markerRef.current?.openPopup();
  };

  const handleMarkerBlur = () => {
    // Da tiempo para mover el cursor del marcador al popup (hay un hueco).
    scheduleBlur();
  };

  useEffect(() => () => cancelBlur(), []);

  useEffect(() => {
    if (!idsMatch(selectedVolcanId, volcan.id)) return;
    const t = window.setTimeout(() => {
      const m = markerRef.current;
      if (!m) return;
      m.openPopup();
      // Solo pan si el volcán no está visible; panear siempre saca el cursor del marcador y cierra el popup.
      if (!map.getBounds().pad(-0.05).contains([lat, lng])) {
        map.panTo([lat, lng], { animate: true, duration: 0.45 });
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [selectedVolcanId, volcan.id, lat, lng, map]);

  // Mantener el popup abierto mientras el cursor esté sobre él.
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return undefined;

    const onPopupOpen = () => {
      const el = marker.getPopup()?.getElement();
      if (!el || el.dataset.volcanHoverBound) return;
      el.dataset.volcanHoverBound = "1";

      const onEnter = () => {
        cancelBlur();
        onVolcanFocus?.(volcan);
      };
      const onLeave = () => {
        scheduleBlur();
      };

      el.addEventListener("mouseenter", onEnter);
      el.addEventListener("mouseleave", onLeave);
      el._volcanPopupCleanup = () => {
        el.removeEventListener("mouseenter", onEnter);
        el.removeEventListener("mouseleave", onLeave);
        delete el.dataset.volcanHoverBound;
        delete el._volcanPopupCleanup;
      };
    };

    const onPopupClose = () => {
      const el = marker.getPopup()?.getElement();
      el?._volcanPopupCleanup?.();
    };

    marker.on("popupopen", onPopupOpen);
    marker.on("popupclose", onPopupClose);
    return () => {
      marker.off("popupopen", onPopupOpen);
      marker.off("popupclose", onPopupClose);
      marker.getPopup()?.getElement()?._volcanPopupCleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bind once per marker mount
  }, [volcan.id]);

  return (
    <Marker
      ref={markerRef}
      position={volcan.__coords}
      icon={volcanIcon}
      eventHandlers={{
        click: (event) => {
          L.DomEvent.stopPropagation(event);
          handleMarkerFocus();
        },
        mouseover: handleMarkerFocus,
        mouseout: handleMarkerBlur,
      }}
    >
      <Popup
        minWidth={220}
        maxWidth={228}
        className="volcan-leaflet-popup"
        closeOnClick={false}
        eventHandlers={{
          add: () => {
            cancelBlur();
            onVolcanFocus?.(volcan);
          },
          remove: () => {
            onVolcanBlur?.(volcan.id);
          },
        }}
      >
        <div
          className={`map-volcan-popup${hasImagen(volcan.imagen_detalle) ? "" : " map-volcan-popup--noimg"}`}
          onMouseEnter={cancelBlur}
          onMouseLeave={scheduleBlur}
        >
          {hasImagen(volcan.imagen_detalle) ? (
            <div className="map-volcan-popup__media">
              <img
                src={getImagenPreviewUrl(volcan.imagen_detalle)}
                alt=""
                className="map-volcan-popup__img"
              />
            </div>
          ) : null}
          <div className="map-volcan-popup__body">
            <div className="map-volcan-popup__title">{volcan.title}</div>
            {coordsText ? (
              <div className="map-volcan-popup__coords">{coordsText}</div>
            ) : null}
            <div className="map-volcan-popup__actions">
              <button
                type="button"
                className="map-volcan-popup__btn"
                onMouseDown={(event) => {
                  // Evita que el mapa interprete el gesto como “sale del popup” antes del click.
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onMoreInfo?.(volcan.id);
                }}
              >
                Más información
              </button>
            </div>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

function VolcanMapa({
  volcanes,
  selectedVolcanId = undefined,
  onMoreInfo,
  onVolcanFocus,
  onVolcanBlur,
  onClearSelection,
}) {
  const { baseLayer } = useMapSettings();
  const points = (volcanes || [])
    .map((v) => ({ ...v, __coords: parseCoords(v.localizacion) }))
    .filter((v) => Array.isArray(v.__coords));

  const tile = useMemo(() => {
    if (baseLayer === "grises") {
      return {
        url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      };
    }
    if (baseLayer === "topografico") {
      return {
        url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
      };
    }
    if (baseLayer === "satelite") {
      return {
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      };
    }
    return {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    };
  }, [baseLayer]);

  return (
    <MapContainer
      center={COLOMBIA_VOLCANES_CENTER}
      zoom={COLOMBIA_VOLCANES_ZOOM}
      zoomSnap={0.2}
      style={{ height: "100%", width: "100%" }}
      attributionControl={{ prefix: LEAFLET_ATTRIBUTION }}
    >
      <TileLayer url={tile.url} />
      {baseLayer === "satelite" ? (
        <Pane name="labels" style={{ zIndex: 450 }}>
          <TileLayer
            url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            pane="labels"
          />
        </Pane>
      ) : null}
      <MapInitialView points={points} />
      <MapPopupCloseSync selectedVolcanId={selectedVolcanId} />
      <MapClearOnBackgroundClick onClear={onClearSelection} />

      {points.map((volcan) => {
        const coordsText = formatCoords(volcan.__coords);
        return (
          <VolcanMarker
            key={volcan.id}
            volcan={volcan}
            coordsText={coordsText}
            selectedVolcanId={selectedVolcanId}
            onMoreInfo={onMoreInfo}
            onVolcanFocus={onVolcanFocus}
            onVolcanBlur={onVolcanBlur}
          />
        );
      })}
    </MapContainer>
  );
}

export default VolcanMapa;
